import { ChangeDetectorRef, DestroyRef, inject, Pipe } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { BehaviorSubject, ObservableInput } from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";

import { isNotNullFilter } from "#shared/utils";

import { TranslateHelpers } from "./helpers";
import { TranslationPath, TranslationPathArray } from "./model";
import { TranslateService } from "./translate.service";

abstract class AsyncPipeBase<TState, TValue> {
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _state$ = new BehaviorSubject<TState | null>(null);
  protected _latestValue: TValue | null = null;
  private _initialized = false;

  private _markForCheckOnValueUpdate = true;

  protected abstract stateEquals(a: TState, b: TState): boolean;
  protected abstract getValue(state: TState): ObservableInput<TValue>;
  protected valueEquals = (a: TValue, b: TValue) => a === b;
  protected projection?: (a: TValue) => TValue;

  private _startSubscription() {
    if (this._initialized) return;
    try {
      this._markForCheckOnValueUpdate = false;
      this._state$
        .pipe(
          isNotNullFilter(),
          switchMap((state) => this.getValue(state)),
          takeUntilDestroyed(this._destroyRef)
        )
        .subscribe((value) => {
          if (this.projection) value = this.projection(value);
          if (this._latestValue !== null && this.valueEquals(value, this._latestValue)) return;
          this._latestValue = value;
          if (this._markForCheckOnValueUpdate) this._cdr.markForCheck();
        });
      this._initialized = true;
    } finally {
      this._markForCheckOnValueUpdate = true;
    }
  }

  protected asyncTransform(state: TState) {
    const lastState = this._state$.getValue();
    if (!lastState || !this.stateEquals(lastState, state)) {
      this._state$.next(state);
      this._startSubscription();
    }
    return this._latestValue;
  }
}

@Pipe({ name: "translate", standalone: true, pure: false })
export class TranslatePipe extends AsyncPipeBase<State, string> {
  private readonly service = inject(TranslateService);
  protected stateEquals = stateEquals;

  protected getValue({ key, params }: State): ObservableInput<string> {
    return this.service.stream(key, params, key);
  }

  transform(key: TranslationPath | TranslationPathArray, params?: any): string {
    return super.asyncTransform({ key, params }) ?? "";
  }
}

@Pipe({ name: "translateOrHide", standalone: true, pure: false })
export class TranslateOrHidePipe extends AsyncPipeBase<State, string> {
  private readonly service = inject(TranslateService);
  protected stateEquals = stateEquals;
  protected _latestValue = "";

  protected getValue({ key, params }: State): ObservableInput<string> {
    return this.service.stream(key, params, "");
  }

  transform(key: TranslationPath, params?: any): string {
    return super.asyncTransform({ key, params }) ?? "";
  }
}

@Pipe({ name: "translateUnsafe", standalone: true, pure: false })
export class TranslateUnsafePipe extends AsyncPipeBase<ArrayState, string> {
  private readonly service = inject(TranslateService);
  protected _latestValue = "";
  protected stateEquals(a: ArrayState, b: ArrayState): boolean {
    return keyEquals(a.path, b.path) && keyEquals(a.path, b.path);
  }

  protected getValue({ path, params }: ArrayState): ObservableInput<string> {
    return this.service.translations$.pipe(
      map((translations) => TranslateHelpers.interpolate(getValueByPath(translations, path), params)),
      tap((translation) => {
        if (translation == null) console!.warn("Translation not found:", path);
      })
    );
  }

  transform(path: [...TranslationPathArray, string], params?: any): string {
    return super.asyncTransform({ path, params }) ?? "";
  }
}

// Helpers

function getValueByPath(target: Object | undefined, keys: string[]) {
  let node = target;
  for (const key of keys) node = node ? node[key] : undefined;
  return node;
}

function keyEquals(a: string | string[], b: string | string[]): boolean {
  return a === b || (Array.isArray(a) ? a.join(".") : a) === (Array.isArray(b) ? b.join(".") : b);
}

function paramsEquals(a: any, b: any): boolean {
  return a === b || JSON.stringify(a) === JSON.stringify(b);
}

interface State {
  key: TranslationPath | TranslationPathArray;
  params?: any;
}
interface ArrayState {
  path: [...TranslationPathArray, string];
  params?: any;
}

function stateEquals(a: State, b: State): boolean {
  return keyEquals(a.key, b.key) && paramsEquals(a.params, b.params);
}
